import { IGeometryInstance } from "../geom"
import { cleanupFeatures } from "./cleanup"
import { getLargestPolygon, getPolygons } from "./polygons"
import { IMapTopologyService } from "./topo/types"
import { ol_Feature, ol_geom } from "@omnia/exports/openlayers";

/**
 * Trims a "zone" (an object containing a feature) along an outer geometry
 */
export function trimZoneGeometry<T extends { readonly feature: ol_Feature }>(zone: T, outerGeometry: ol_geom.Geometry, topo: IMapTopologyService) {

    let geom = zone.feature.getGeometry()

    if (!topo.contains(outerGeometry, geom)) {

        const intersection = topo.intersection(outerGeometry, geom)

        if (intersection && intersection.isValid() && (intersection.getDimension() == 2)) {

            geom = getLargestPolygon(intersection.getOpenLayersGeometry())

            if (geom) {
                zone.feature.setGeometry(geom)
            }

        }

    }
}


/**
 * Remove potential overlaps between one zone and a collection of others
 *
 * This may lead to the complete removal one existing zones (if the target zone completely convers another one),
 * or to the creation of new zones (if geometry collections or multipolygons are created in the process)
 *
 * @param targetZone Target zone
 * @param zones All potential other overlapping zones
 * @param removeZones Handler to remove zones
 * @param createNewZone Handler to create a new zone
 */
export function removeOverlaps<Zone extends { readonly feature: ol_Feature }>(
    targetZone: Zone,
    zones: Zone[],
    removeZones: (zones: Zone[]) => void,
    createNewZone: (geom: ol_geom.Geometry, initialZone: Zone) => void,
    topo: IMapTopologyService
) {

    let targetGeometry = targetZone.feature.getGeometry()
    let newZones: { initialZone: Zone, geometry: ol_geom.Geometry }[] = []
    let deletedZones: Zone[] = []

    zones.filter(z => z != targetZone).forEach(z => {

        const zoneGeometry = z.feature.getGeometry()

        if (!topo.disjoint(targetGeometry, zoneGeometry)) {

            const diff = topo.difference(zoneGeometry, [targetGeometry])

            if (diff && diff.isValid()) {

                let parts = getPolygons(diff.getOpenLayersGeometry())

                if (!parts.length) {

                    deletedZones.push(z)

                }
                else {

                    z.feature.setGeometry(parts[0])

                    if (parts.length > 1) {

                        newZones = newZones.concat(parts.filter((p, index) => index > 0).map(p => {
                            return {
                                initialZone: z,
                                geometry: p
                            }
                        }))

                    }

                }

            }

        }

    })

    removeZones(deletedZones)

    for (const i of newZones) {
        createNewZone(i.geometry, i.initialZone)
    }

}

export function createMissingZones<Zone extends { readonly feature: ol_Feature }>(
    zones: Zone[],
    topo: IMapTopologyService,
    outerGeometry: ol_geom.Geometry | IGeometryInstance,
    createNewZone: (geom: ol_geom.Geometry) => void,
) {

    try {

        const features = zones.map(z => z.feature)

        cleanupFeatures(features)

        const outer = topo.getGeometry(outerGeometry)

        const geometries = features.map(f => f.getGeometry());

        const diff = topo.difference(outer, geometries);

        const polygons = getPolygons(diff.getOpenLayersGeometry());

        for (const p of polygons) {
            createNewZone(p)
        }

    }
    catch (e) {

        console.error("createMissingZones", e)

    }

}
